import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import TeamColorsPicker from "./TeamColorsPicker";
import { saveTeam } from "../api/team";
import { fetchPlayers, savePlayers } from "../api/players";
import ControlField from "./team/ControlField";
import { TeamSidePanel } from "./team/TeamSidePanel";
import { RenameTeamModal } from "./team/RenameTeamModal";
import { InfoTip } from "./InfoTip";
import { useTeam } from "../context/TeamContext";
import { useAuth } from "../auth/AuthContext";
import { useTactics } from "../hooks/useTactics";
import { Save, Loader, FlaskConical, SignalLow, SignalMedium, SignalHigh, Users, ChevronDown } from "lucide-react";
import grass from "../assets/textures/grass2.jpg";
import { api } from "../api/clientInstance";
import { apiErrorMessage } from "../utils/apiError";
import GamePlayer from "./GamePlayer";
import { playerFromBackend, playerToBackend } from "../utils/teamModel";
import { BASIC_STATEMENTS } from "./tactic/basicTactic";
import { serializeStatements } from "./tactic/tacticModel";
import { useTokens } from "../hooks/useTokens";
import { InspectorTactic } from "./tactic/InspectorTactic";
import { frameDecision, partialState, narrationSteps } from "./tactic/inspectorTrace";
import "./TeamFormPage.css";

const DEFAULT_POSITIONS = [
  { x: 0.5,  y: 0.05 },
  { x: 0.2,  y: 0.16 },
  { x: 0.5,  y: 0.16 },
  { x: 0.8,  y: 0.16 },
  { x: 0.25, y: 0.29 },
  { x: 0.75, y: 0.29 },
  { x: 0.5,  y: 0.39 },
];

// Difficulty ramp for the autotest opponents (system teams), mapped by name.
// Signal low/med/high = a "level meter" that visibly climbs; colour reinforces it.
const OPPONENT_DIFFICULTY = {
  "Rookies FC":     { Icon: SignalLow,    color: "#2e7d32", rank: 0 }, // verde
  "Average United": { Icon: SignalMedium, color: "#f9a825", rank: 1 }, // ámbar
  "Elite Squad":    { Icon: SignalHigh,   color: "#c62828", rank: 2 }, // rojo
};

const defaultPlayers = () =>
  DEFAULT_POSITIONS.map((pos, i) => ({ id: null, frontendId: i, ...pos, name: "", tacticId: null, isStarter: false, isGoalkeeper: false }));

const NARR_BASE_MS = 1200; // base pace at 1×; the speed control divides it (1200/600/400)

export default function TeamFormPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isGuest = !!user?.is_guest;   // guests: show a fixed "My Team", no rename (name set at registration)
  const { team, setTeam, loading: teamLoading, refreshReadiness } = useTeam();
  const { tactics, loading: tacticsLoading, error: tacticsError, create, update, remove } = useTactics(team?.id);
  const { tokens: langTokens } = useTokens();

  const [form, setForm]           = useState({ name: "", localColor: "#ff0000", awayColor: "#0000ff" });
  const [players, setPlayers]     = useState(defaultPlayers);
  const [selectedId, setSelectedId] = useState(null);
  const [renaming, setRenaming]   = useState(false);
  const [createError, setCreateError] = useState("");
  const [saving, setSaving]       = useState(false);
  const [saveError, setSaveError] = useState("");
  const [testing, setTesting]         = useState(false);
  const [testFrames, setTestFrames]   = useState(null);
  const [testEv, setTestEv]           = useState(null);   // current event, shown above the pitch
  const [testTrace, setTestTrace]     = useState(null);   // inspector: {player: [entries]}
  const [inspectPlayer, setInspectPlayer] = useState(null); // local player index or null
  const [inspectFrame, setInspectFrame]   = useState(0);    // current decision frame
  // Inspecting a player IS slow mode: selecting narrates its decisions; the
  // reset / "None" exits. No separate toggle.
  const [narrating, setNarrating]   = useState(false);      // freezing + revealing a decision
  const [narrStep, setNarrStep]     = useState(0);          // conditions revealed so far
  const [narrSpeed, setNarrSpeed]   = useState(1);          // narration pace multiplier: 1|2|3
  const [inspectorOpen, setInspectorOpen] = useState(false); // right drawer visible
  const lastNarratedFrom = useRef(null);
  const [testSelfPlay, setTestSelfPlay] = useState(true);
  const [testError, setTestError]     = useState("");
  const [opponents, setOpponents]   = useState([]);
  const [showPicker, setShowPicker] = useState(false);
  const pickerRef = useRef(null);

  const updateField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  useEffect(() => {
    if (!team) return;
    setForm({ name: team.name || "", localColor: team.localColor || "#ff0000", awayColor: team.awayColor || "#0000ff" });
  }, [team]);

  useEffect(() => {
    if (!team?.id) return;
    api("/game/autotest/opponents").then(setOpponents).catch(() => {});
  }, [team?.id]);

  // Load saved formation from backend
  useEffect(() => {
    if (!team?.id) return;
    fetchPlayers()
      .then((data) => {
        if (data.length > 0)
          setPlayers(data.map(playerFromBackend));
      })
      .catch((err) => console.error("Failed to load players:", err));
  }, [team?.id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setCreateError("");
    const name = team ? team.name : form.name;
    try {
      // On CREATE, ship the default tactic tokens so the backend builds a
      // ready-to-play team (default formation, GeneralTactic assigned to all,
      // goalkeeper + server set). The tactic grammar stays here, not duplicated
      // server-side. On UPDATE we only touch the basic team data.
      const payload = { Name: name, LocalColor: form.localColor, AwayColor: form.awayColor };
      if (!team) payload.DefaultTactic = serializeStatements(BASIC_STATEMENTS);
      const data = await saveTeam(payload, team ? "PUT" : "POST");
      if (!team) setTeam({ id: data.id, name: data.name, localColor: data.local_color, awayColor: data.away_color });
    } catch (err) {
      setCreateError(err.message === "TEAM_NAME_TAKEN" ? t("team.nameTaken") : err.message);
    }
  };

  const handleRename = async (newName) => {
    await saveTeam({ Name: newName, LocalColor: form.localColor, AwayColor: form.awayColor }, "PUT");
    setForm((prev) => ({ ...prev, name: newName }));
  };

  const handleSaveFormation = async () => {
    setSaving(true);
    setSaveError("");
    try {
      const saved = await savePlayers(players.map(playerToBackend));
      setPlayers(saved.map(playerFromBackend));
      refreshReadiness();
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!showPicker) return;
    const handler = (e) => { if (pickerRef.current && !pickerRef.current.contains(e.target)) setShowPicker(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showPicker]);

  const handleAutotest = async (opponentId = null) => {
    setShowPicker(false);
    setTesting(true);
    setTestError("");
    setTestFrames(null);
    setTestTrace(null);
    setInspectPlayer(null);
    setInspectFrame(0);
    setInspectorOpen(false);
    setTestSelfPlay(!opponentId);
    try {
      const url = opponentId ? `/game/autotest?opponent_team_id=${opponentId}` : "/game/autotest";
      const result = await api(url, { method: "POST" });
      setTestFrames(result.frames);
      setTestTrace(result.trace ?? {});
    } catch (e) {
      setTestError(apiErrorMessage(e, t));
    } finally {
      setTesting(false);
    }
  };

  const handleAutotestClick = () => {
    if (opponents.length > 0) setShowPicker((v) => !v);
    else handleAutotest();
  };

  const handleMove           = (id, x, y) => setPlayers((prev) => prev.map((p) => (p.frontendId === id ? { ...p, x, y } : p)));
  const handleToggleStarter  = (id) => setPlayers((prev) => prev.map((p) => ({ ...p, isStarter: p.frontendId === id ? !p.isStarter : false })));
  const handleToggleGoalkeeper = (id) => setPlayers((prev) => prev.map((p) => ({ ...p, isGoalkeeper: p.frontendId === id ? !p.isGoalkeeper : false })));
  const handleAssignTactic   = (playerId, tacticId) => setPlayers((prev) => prev.map((p) => (p.frontendId === playerId ? { ...p, tacticId } : p)));
  const handleRenamePlayer   = (playerId, name) => setPlayers((prev) => prev.map((p) => (p.frontendId === playerId ? { ...p, name } : p)));

  const handleDeleteTactic = async (id) => {
    await remove(id);
    setPlayers((prev) => prev.map((p) => (p.tacticId === id ? { ...p, tacticId: null } : p)));
  };

  // Empty-state quick start: create the generic tactic (persisted immediately)
  // and optionally assign it to every player. Assignment is LOCAL state, saved
  // by the existing "Save formation" button (see Docs/basic-tactic-feature.txt).
  // The dialog awaits this promise, so errors surface there.
  const handleCreateBasicTactic = async (name, assignAll) => {
    const tactic = await create(name);
    await update(tactic.id, BASIC_STATEMENTS);
    if (assignAll) {
      setPlayers((prev) => prev.map((p) => ({ ...p, tacticId: tactic.id })));
    }
  };

  // reset the narration cursor whenever the selection changes
  useEffect(() => {
    lastNarratedFrom.current = null;
    setNarrating(false);
    setNarrStep(0);
  }, [inspectPlayer]);

  // Inspecting a player is slow mode: when the field reaches a frame where the
  // decision CHANGES, freeze it (blockAdvance) and reveal its conditions one by one.
  useEffect(() => {
    if (inspectPlayer == null || !testTrace) { setNarrating(false); return; }
    const d = frameDecision(testTrace, inspectPlayer, inspectFrame);
    if (!d) { setNarrating(false); return; }
    if (d.from !== lastNarratedFrom.current) {
      lastNarratedFrom.current = d.from;
      setNarrStep(0);
      setNarrating(true);
    }
  }, [inspectFrame, inspectPlayer, testTrace]);

  // While narrating, reveal one more step every NARR_BASE_MS / speed; unfreeze when done.
  useEffect(() => {
    if (!narrating || inspectPlayer == null) return;
    const d = frameDecision(testTrace, inspectPlayer, inspectFrame);
    if (narrStep >= narrationSteps(d)) { setNarrating(false); return; }
    const id = setTimeout(() => setNarrStep((s) => s + 1), NARR_BASE_MS / narrSpeed);
    return () => clearTimeout(id);
  }, [narrating, narrStep, narrSpeed, inspectPlayer, inspectFrame, testTrace]);

  if (teamLoading) return <p>Loading...</p>;

  return (
    <div className="team-page">
      <form onSubmit={handleSubmit}>
        <div className="team-form-header">
          {team ? (
            <div className="team-name-display">
              <span className="team-name-text">{isGuest ? t("guest.myTeam") : (form.name || team.name)}</span>
              {!isGuest && (
                <button type="button" className="team-rename-btn" onClick={() => setRenaming(true)} title="Rename team">✎</button>
              )}
              <button className="team-save-btn" onClick={handleSaveFormation} disabled={saving} title={t("save")}>
                {saving ? <Loader size={16} className="spin" /> : <Save size={16} />}
              </button>
              {saveError && <span className="team-save-error">{saveError}</span>}
            </div>
          ) : (
            <div style={{ display: "flex", alignItems: "center", gap: "0.3rem", flexWrap: "wrap" }}>
              <input type="text" value={form.name} onChange={(e) => updateField("name", e.target.value)} placeholder="Team Name" required />
              <InfoTip message={t("team.namePolicy")} />
              <button disabled={!form.name.trim()} type="submit">Create Team</button>
              {createError && <span className="team-save-error">{createError}</span>}
            </div>
          )}
          <div className="team-colors-actions">
            <TeamColorsPicker
              colors={{ local: form.localColor, away: form.awayColor }}
              onChange={(type, value) => updateField(type === "local" ? "localColor" : "awayColor", value)}
            />
          </div>
        </div>
      </form>

      {!team && (
        <p className="team-create-hint">{t("team.createHint")}</p>
      )}

      {renaming && (
        <RenameTeamModal currentName={form.name || team.name} onSave={handleRename} onClose={() => setRenaming(false)} />
      )}

      {team && (
        <div className="team-testbar">
          <div ref={pickerRef} className="team-autotest-wrap">
            <button className="team-testbtn" onClick={handleAutotestClick} disabled={testing} title={t("team.autotest")}>
              {testing ? <Loader size={18} className="spin" /> : <FlaskConical size={18} />}
              <span className="team-testbtn-label">{t("team.autotestCta")}</span>
              <ChevronDown size={16} className="team-testbtn-caret" />
            </button>
            {showPicker && (
              <ul className="team-autotest-picker">
                <li onClick={() => handleAutotest()}>
                  <Users size={16} color="#9e9e9e" style={{ marginRight: 8, flexShrink: 0 }} />
                  {t("team.autotestSelf")}
                </li>
                {[...opponents]
                  .sort((a, b) => (OPPONENT_DIFFICULTY[a.name]?.rank ?? 99) - (OPPONENT_DIFFICULTY[b.name]?.rank ?? 99))
                  .map((o) => {
                  const diff = OPPONENT_DIFFICULTY[o.name];
                  const Icon = diff?.Icon;
                  return (
                    <li key={o.id} onClick={() => handleAutotest(o.id)}>
                      {Icon && (
                        <Icon size={16} color={diff.color} style={{ marginRight: 8, flexShrink: 0 }} />
                      )}
                      {o.name}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          {testError && <span className="team-save-error">{testError}</span>}
        </div>
      )}

      {team && (
        <div className="team-editor-layout">
          <div className="team-field-section">
            <ControlField
              players={players}
              tactics={tactics}
              selectedId={selectedId}
              teamColor={form.localColor}
              background={grass}
              onSelect={(id) => setSelectedId(id)}
              onMove={handleMove}
            />
          </div>

          <div className="team-tactics-section">
            <TeamSidePanel
              players={players}
              selectedId={selectedId}
              tactics={tactics}
              tacticsLoading={tacticsLoading}
              tacticsError={tacticsError}
              onToggleStarter={handleToggleStarter}
              onToggleGoalkeeper={handleToggleGoalkeeper}
              onAssignTactic={handleAssignTactic}
              onRenamePlayer={handleRenamePlayer}
              canCreate={!!team?.id}
              onCreate={create}
              onUpdate={update}
              onDelete={handleDeleteTactic}
              onCreateBasic={handleCreateBasicTactic}
            />
          </div>
        </div>
      )}

      {testFrames && (() => {
        const inspectedTactic = inspectPlayer != null
          ? tactics.find((tt) => tt.id === players[inspectPlayer]?.tacticId)
          : null;
        const decision = inspectPlayer != null
          ? frameDecision(testTrace ?? {}, inspectPlayer, inspectFrame)
          : null;
        const totalSteps = narrationSteps(decision);
        // reveal progressively while narrating this decision; otherwise show it whole
        const revealCount = narrating ? narrStep : totalSteps;
        const inspectState = inspectPlayer != null ? partialState(decision, revealCount) : null;
        // Give the inspector the tactic names so a CALL action renders as @name.
        const inspTokens = { ...langTokens, callables: tactics.map((tt) => ({ id: tt.id, name: tt.name })) };
        // Labelled divider so each tactic block in the inspector is clearly named
        // (the player's own tactic, then each called tactic). "——— name ———".
        const sectionDivider = (name) => (
          <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "10px 0 6px", color: "#8a8a8a", fontWeight: 700, fontSize: 12 }}>
            <span style={{ flex: 1, height: 2, background: "currentColor", borderRadius: 1 }} />
            <span>{name}</span>
            <span style={{ flex: 1, height: 2, background: "currentColor", borderRadius: 1 }} />
          </div>
        );
        const hasTrace = testTrace && Object.keys(testTrace).length > 0;

        const drawerOpen = hasTrace && inspectorOpen;
        const toggleDrawer = () => {
          if (inspectorOpen) { setInspectorOpen(false); setInspectPlayer(null); }
          else setInspectorOpen(true);
        };

        return (
        <div
          className="autotest-backdrop"
          onClick={(e) => { if (e.target === e.currentTarget) setTestFrames(null); }}
        >
          <div className="autotest-modal">
            <div className="autotest-header">
              <button className="autotest-back" onClick={() => setTestFrames(null)} title={t("inspector.close")}>✕</button>
              {(() => {
                const sc = testFrames[inspectFrame]?.score ?? testFrames[0]?.score ?? [0, 0];
                return (
                  <div className="autotest-score">
                    <span style={{ color: form.localColor }}>{sc[0]}</span>
                    <span className="autotest-score-sep">–</span>
                    <span style={{ color: form.awayColor }}>{sc[1]}</span>
                  </div>
                );
              })()}
              {hasTrace ? (
                <button
                  className="inspector-toggle"
                  onClick={toggleDrawer}
                  title={t("inspector.player")}
                >{t("inspector.title")} {drawerOpen ? "‹" : "›"}</button>
              ) : (
                <span className="autotest-header-spacer" aria-hidden="true">›</span>
              )}
              <div className="autotest-event">
                {testEv && (
                  <>
                    <span className="autotest-event-dot" style={{ background: testEv.team === 0 ? form.localColor : form.awayColor }} />
                    <span className="autotest-event-text">{testEv.label} {t(`game.ev${testEv.type}`)}{testEv.type === 3 ? " 💥" : ""}</span>
                  </>
                )}
              </div>
            </div>
            <div className={`autotest-body${drawerOpen ? " autotest-body--drawer" : ""}`}>
              <GamePlayer
                frames={testFrames}
                localColor={form.localColor}
                awayColor={form.awayColor}
                localPlayerNames={players.map((p) => p.name)}
                awayPlayerNames={testSelfPlay ? players.map((p) => p.name) : []}
                highlightIndex={drawerOpen ? inspectPlayer : null}
                onFrame={setInspectFrame}
                onEvent={setTestEv}
                blockAdvance={drawerOpen && inspectPlayer != null && narrating}
                vertical
                maxHeight={Math.round((window.innerHeight - 96) * 0.82)}
                autoPlay
              />
              {drawerOpen && (
                <div className="inspector-drawer">
                  <div className="inspector-drawer-head">
                    <select
                      className="inspector-select"
                      value={inspectPlayer == null ? "" : inspectPlayer}
                      onChange={(e) => setInspectPlayer(e.target.value === "" ? null : Number(e.target.value))}
                    >
                      <option value="">{t("inspector.pick")}</option>
                      {players.map((p, i) => (
                        <option key={p.frontendId ?? i} value={i}>
                          {p.name ? p.name : `#${i + 1}`}
                        </option>
                      ))}
                    </select>
                    {inspectPlayer != null && (
                      <>
                        <button className="inspector-reset" onClick={() => setInspectPlayer(null)} title={t("inspector.reset")}>↺</button>
                        <button
                          className="inspector-speed"
                          onClick={() => setNarrSpeed((s) => (s >= 3 ? 1 : s + 1))}
                          title={t("inspector.speed")}
                        >{narrSpeed}×</button>
                      </>
                    )}
                  </div>
                  {inspectPlayer != null && (inspectedTactic ? (
                    <>
                      {sectionDivider(inspectedTactic.name)}
                      <InspectorTactic
                        statements={inspectedTactic.statements}
                        tokens={inspTokens}
                        state={inspectState}
                      />
                    </>
                  ) : (
                    <p className="inspector-empty">{t("inspector.noTactic")}</p>
                  ))}
                  {/* Tactic calls: when this decision stepped into a called tactic
                      (a tacticId >= 0 in the trace), show that tactic too, painted
                      with ITS own scoped state. See Docs/tactic-calls.txt. */}
                  {inspectPlayer != null && decision &&
                    [...new Set((decision.visited || []).map((v) => v[0]).concat(decision.firedTactic))]
                      .filter((id) => id != null && id >= 0)
                      .map((cid) => {
                        const callee = tactics.find((tt) => tt.id === cid);
                        if (!callee) return null;
                        return (
                          <div key={cid} className="inspector-callee" style={{ marginTop: 6 }}>
                            {sectionDivider(callee.name)}
                            <InspectorTactic
                              statements={callee.statements}
                              tokens={inspTokens}
                              state={partialState(decision, revealCount, cid)}
                            />
                          </div>
                        );
                      })}
                  {inspectPlayer != null && decision && inspectFrame > decision.from && (
                    <p className="inspector-rle-hint">{t("inspector.rleHint")}</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        );
      })()}
    </div>
  );
}
